import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeroCalculator } from "./HeroCalculator";

const IMG_COUPLE =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_texas-mortgage/artifacts/uvnd5j0o_Firefly_2.%20Working%20the%20numbers%20together%20%2816-9%29__A%20couple%20at%20a%20dining%20table%2C%20the%20woman%20typing%20%20568734.webp";
const IMG_PHONE =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_texas-mortgage/artifacts/n50wd3ek_Firefly_Gemini%20Flash_4.%20How%20it%20works%20%E2%80%94%20planning%20on%20the%20phone%20%281-1%29__Close%20over-the-shoulder%20shot%20of%20hands%20%20568734.webp";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="section-hero"
      className="relative overflow-hidden bg-ink"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 600px at 72% 12%, rgba(47,169,138,0.13), transparent 60%), radial-gradient(700px 500px at 8% 85%, rgba(232,199,126,0.06), transparent 60%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 pt-32 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:pb-20 lg:pt-40">
        <motion.div style={{ y: yText }}>
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="font-mono text-xs font-medium tagline-track"
            style={{ color: "#8FB5A8" }}
          >
            Texas homebuyers
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[56px]"
          >
            <Line delay={0.35}>The payment</Line>
            <Line delay={0.47}>
              you&rsquo;ll <span className="text-lens-bright">actually</span>
            </Line>
            <Line delay={0.59}>pay.</Line>
          </h1>

          <motion.p
            data-testid="hero-subhead"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
            className="mt-6 max-w-[34rem] font-body text-base leading-relaxed sm:text-lg"
            style={{ color: "#B9CBC5" }}
          >
            National calculators quote a national average. Your payment
            depends on your credit band, your county&rsquo;s tax rate, the
            homestead exemption, MUD districts, and Texas&rsquo;s real
            insurance market. Mortlens prices all of it — and shows the math.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1.0 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => onNavigate("#waitlist")}
              className="rounded-lg bg-lens px-7 py-3.5 font-body text-base font-semibold text-white transition-all duration-300 hover:bg-lens-bright hover:shadow-[0_0_32px_rgba(47,169,138,0.4)] active:scale-[0.97]"
            >
              Get my real payment
            </button>
            <button
              data-testid="hero-secondary-button"
              onClick={() => onNavigate("#how")}
              className="rounded-lg border border-white/20 px-7 py-3.5 font-body text-base font-medium text-white/80 transition-all duration-300 hover:border-white/50 hover:text-white active:scale-[0.97]"
            >
              See how it works
            </button>
          </motion.div>

          <motion.p
            data-testid="hero-trust-line"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-8 font-mono text-[13px]"
            style={{ color: "#8FB5A8" }}
          >
            No lenders paid to be here. Every number shows its math.
          </motion.p>
        </motion.div>

        <HeroCalculator />
      </div>

      <motion.div
        style={{ y: yPhoto }}
        initial={{ opacity: 0, clipPath: "inset(20% 6% 20% 6%)" }}
        animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.1, ease: EASE, delay: 1.2 }}
        className="relative mx-auto max-w-7xl px-5 pb-24 sm:px-8"
      >
        <div className="relative h-56 overflow-hidden rounded-lg border border-white/10 sm:h-72">
          <img
            data-testid="hero-image-couple"
            src={IMG_COUPLE}
            alt="A couple working the numbers together at their dining table"
            className="h-full w-full object-cover object-[50%_38%]"
            loading="eager"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(12,31,44,0.55) 0%, rgba(12,31,44,0.0) 45%, rgba(12,31,44,0.35) 100%)",
            }}
          />
          <div className="absolute bottom-4 left-4 rounded-md bg-ink/70 px-3 py-1.5 backdrop-blur-sm">
            <p className="font-mono text-[10px] tracking-[0.2em] text-white/70">
              RUNNING THE REAL NUMBER
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.5 }}
          className="absolute -top-10 right-10 hidden w-40 overflow-hidden rounded-lg border border-white/15 shadow-[0_20px_48px_rgba(0,0,0,0.45)] lg:block"
        >
          <img
            data-testid="hero-image-phone"
            src={IMG_PHONE}
            alt="Hands holding a phone showing a payment breakdown"
            className="aspect-square h-auto w-full object-cover"
            loading="lazy"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
